import { useState, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';

/**
 * Enhanced file upload hook with error recovery and atomic transaction support
 * @param {string} bucket - Storage bucket name
 * @param {string} folder - Folder path (usually user_id or artist_id)
 * @param {Object} options - Upload options
 */
export const useFileUpload = (bucket, folder, options = {}) => {
  const [uploading, setUploading]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [error, setError]           = useState(null);
  const [url, setUrl]               = useState(null);
  const abortControllerRef          = useRef(null);
  const retryCountRef               = useRef({});
  const MAX_RETRIES                 = 3;

  const upload = useCallback(async (file, fileName = null) => {
    if (!file) {
      setError('No file provided');
      return null;
    }

    // Validate file size (default 10MB limit)
    const maxSize = options.maxSize || 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size exceeds ${maxSize / 1024 / 1024}MB limit`);
      return null;
    }

    // Validate file type
    if (options.acceptedTypes && !options.acceptedTypes.includes(file.type)) {
      setError(`Invalid file type. Accepted: ${options.acceptedTypes.join(', ')}`);
      return null;
    }

    setUploading(true);
    setError(null);
    setProgress(0);
    
    // Generate a unique file ID for retry tracking
    const fileId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    retryCountRef.current[fileId] = 0;
    
    // Create a new AbortController for this upload
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    
    const uploadWithRetry = async (attempt = 0) => {
      try {
        const ext = file.name.split('.').pop();
        const path = `${folder}/${fileName || `${Date.now()}.${ext}`}`;
        
        const { data, error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: options.upsert ?? true,
            contentType: file.type,
            duplex: 'half',
            signal: abortControllerRef.current?.signal,
          });
        
        if (uploadError) {
          // Network error or server error - retry
          if (attempt < MAX_RETRIES && 
              (uploadError.message?.includes('network') || 
               uploadError.statusCode >= 500)) {
            retryCountRef.current[fileId] = attempt + 1;
            const backoff = Math.min(1000 * Math.pow(2, attempt), 10000);
            await new Promise(resolve => setTimeout(resolve, backoff));
            return uploadWithRetry(attempt + 1);
          }
          throw uploadError;
        }
        
        // Get public URL
        const { data: urlData } = supabase.storage
          .from(bucket)
          .getPublicUrl(data.path);
        
        setUrl(urlData.publicUrl);
        setProgress(100);
        
        return {
          path: data.path,
          url: urlData.publicUrl,
          bucket,
          size: file.size,
          type: file.type,
          retries: retryCountRef.current[fileId] || 0,
        };
      } catch (err) {
        // Abort error - don't report it as an error
        if (err.name === 'AbortError') {
          console.log('Upload aborted by user');
          return null;
        }
        
        console.error('Upload error:', err);
        setError(err.message);
        return null;
      }
    };
    
    try {
      const result = await uploadWithRetry();
      return result;
    } finally {
      setUploading(false);
      delete retryCountRef.current[fileId];
    }
  }, [bucket, folder, options]);

  const uploadMultiple = useCallback(async (files) => {
    const results = [];
    const errors = [];
    
    for (const file of files) {
      const result = await upload(file);
      if (result) results.push(result);
      else errors.push(file.name);
    }
    
    if (errors.length > 0) {
      setError(`Failed to upload: ${errors.join(', ')}`);
    }
    
    return results;
  }, [upload]);
  
  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setUploading(false);
    setProgress(0);
  }, []);

  const remove = useCallback(async (filePath) => {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([filePath]);
      
      if (error) throw error;
      
      if (url === supabase.storage.from(bucket).getPublicUrl(filePath).data.publicUrl) {
        setUrl(null);
      }
      
      return true;
    } catch (err) {
      console.error('Remove error:', err);
      setError(err.message);
      return false;
    }
  }, [bucket, url]);

  return {
    upload,
    uploadMultiple,
    remove,
    cancelUpload,
    uploading,
    progress,
    error,
    url,
    reset: () => {
      setUploading(false);
      setProgress(0);
      setError(null);
      setUrl(null);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    },
  };
};